'use client';

/**
 * `<InboxBell>` — иконка-колокольчик с badge непрочитанных (REQ-INB-001, INB §2).
 *
 * Контракт inbox §C.8.1. Рендерится в `<Header>` (слева от Switch темы).
 *
 * Цветовая система: badge — брендовый градиент `brandGradient` (parity темы),
 * индикатор «новое» — `starColor` (оранжевый #ff8f3d, parity спецзначка «непрочитано»
 * из evolve-news-feed §B7). Все цвета через `theme.other`-токены + `light-dark()`,
 * чтобы корректно работать в тёмной теме (без дефолтных Mantine-цветов).
 *
 * Состояния (parity INB §2.3):
 * - `unread_count === 0` → `IconBell` (контурная), badge скрыт полностью.
 * - `1 ≤ unread_count ≤ 99` → `IconBellFilled`, badge с числом.
 * - `unread_count > 99` → `IconBellFilled`, badge «99+».
 * - `isLoading && lastFetchAt === null` → `IconBell` + skeleton Loader (первый fetch).
 * - `error === true` → последнее известное значение (stale), тихий retry.
 *
 * a11y: `aria-label="Уведомления"` (динамический — добавляет «, N новых» через
 * live-регион `aria-live="polite"` sr-only); `Enter`/`Space` открывают, `Esc` закрывает,
 * focus возвращается на кнопку (Mantine Popover a11y parity).
 */

import { useState } from 'react';
import { ActionIcon, Loader, Popover, useMantineTheme } from '@mantine/core';
import { IconBell, IconBellFilled } from '@tabler/icons-react';

import { useInbox } from '@/lib/inbox/useInbox';
import { InboxDropdown } from './InboxDropdown';

export function InboxBell() {
  const theme = useMantineTheme();
  const { unread_count, isLoading, lastFetchAt, requestNotificationPermission } =
    useInbox();
  const [open, setOpen] = useState(false);
  const [permissionAsked, setPermissionAsked] = useState(false);

  const hasUnread = unread_count > 0;
  const showSkeleton = isLoading && lastFetchAt === null; // первый fetch
  const badgeText = unread_count > 99 ? '99+' : String(unread_count);

  // Запрос permission по первому клику (AC-INB-010.3): только если permission
  // 'default' и есть непрочитанные; повторно не дёргаем (permissionAsked guard).
  const handleOpen = (willOpen: boolean) => {
    setOpen(willOpen);
    if (
      willOpen &&
      !permissionAsked &&
      hasUnread &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default'
    ) {
      setPermissionAsked(true);
      void requestNotificationPermission();
    }
  };

  // Иконка: skeleton во время первого fetch; filled если есть непрочитанные.
  const Icon = showSkeleton ? IconBell : hasUnread ? IconBellFilled : IconBell;

  return (
    <Popover
      opened={open}
      onChange={handleOpen}
      position="bottom-end"
      withArrow
      shadow="md"
      width="auto"
      trapFocus
      returnFocus // фокус возвращается на кнопку при закрытии (a11y)
      styles={{
        dropdown: {
          // light-dark() — SSR-stable (parity theme.ts getInputStyles/getComboboxInputStyles).
          background: 'light-dark(rgba(255, 255, 255, 0.98), rgba(38, 28, 64, 0.98))',
          border: '1px solid light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.18))',
          backdropFilter: 'blur(14px)',
        },
      }}
    >
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          size="lg"
          aria-label={hasUnread ? `Уведомления, ${unread_count} новых` : 'Уведомления'}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => handleOpen(!open)}
          style={{ position: 'relative' }}
        >
          {showSkeleton ? (
            <Loader size="xs" color={theme.other.brandAccentStart} />
          ) : (
            <Icon size={22} />
          )}
          {/* Badge: брендовый градиент (parity темы), скрыт полностью при
              unread_count === 0 (AC-INB-001.3). */}
          {!showSkeleton && hasUnread ? (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 999,
                background: theme.other.brandGradient,
                color: theme.white,
                fontSize: 10,
                fontWeight: 800,
                lineHeight: '16px',
                textAlign: 'center',
                pointerEvents: 'none',
                boxShadow: '0 2px 6px rgba(123, 77, 255, 0.30)',
              }}
            >
              {badgeText}
            </span>
          ) : null}
          {/* Live-регион для скринридеров (AC-INB-011.1). */}
          <span
            aria-live="polite"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {hasUnread ? `Уведомления, ${unread_count} новых` : ''}
          </span>
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <InboxDropdown onClose={() => setOpen(false)} />
      </Popover.Dropdown>
    </Popover>
  );
}
